import { Injectable } from '@angular/core';
import { takeWhile, map, exhaustMap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { interval, Observable, Subscription } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { PProgressBarDialogComponent } from './p-progress-bar-dialog.component';
import { ProgressStatus } from './progress-status';
import { ProgressStatusType } from './progress-status-type.enum';

@Injectable({ providedIn: 'root' })
export class LoadingProgressBarService {
    constructor(private http: HttpClient, private translate: TranslateService, private dialog: MatDialog) {}
    private dialogRef?: MatDialogRef<PProgressBarDialogComponent>;
    public initProgressBar<T, P extends Record<string, string>>(
        uuid: string,
        callback?: (data: T, params?: P) => void,
        params?: P,
        ignoreCompletePercentage = false
    ): Subscription {
        this.dialogRef = this.dialog.open(PProgressBarDialogComponent, {
            height: '100%',
            width: '100%',
            panelClass: 'p-progress-bar-dialog',
            data: { value: 0, title: this.translate.instant('ProgressBar.starting') }
        });

        return this.getStatusWithInterval<T, P>(uuid, ignoreCompletePercentage, params, 1000).subscribe(
            (progress: ProgressStatus<T>) => {
                if (this.dialogRef && this.dialogRef.componentInstance) {
                    this.dialogRef.componentInstance.data = {
                        value: progress.percentage,
                        title: progress.mainTitle,
                        description: progress.title
                    };
                }

                if (this.dialogRef && this.isCompleted(progress, ignoreCompletePercentage)) {
                    this.dialogRef.close();
                    if (progress.data != null && callback) {
                        callback(progress.data, params);
                    }
                }
            },
            (err) => {
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
            }
        );
    }

    private getStatusWithInterval<T, P extends Record<string, string>>(
        uuid: string,
        ignoreCompletePercentage: boolean,
        params?: P,
        period?: number
    ): Observable<ProgressStatus<T>> {
        return interval(period).pipe(
            exhaustMap(() => this.getProgressStatus<T, P>(uuid, params)),
            map((response) => response),
            takeWhile((progress) => !this.isCompleted(progress, ignoreCompletePercentage), true)
        );
    }

    private getProgressStatus<T, P extends Record<string, string>>(uuid: string, params?: P): Observable<ProgressStatus<T>> {
        let httpParams = new HttpParams();
        for (const key in params) {
            if (params[key] !== undefined && params[key] !== null) {
                httpParams = httpParams.set(key, params[key]);
            }
        }
        return this.http.get<ProgressStatus<T>>(`/rest/common/progressStatus/${uuid || 0}`, { params: httpParams });
    }

    private isCompleted<T>(progress: ProgressStatus<T>, ignoreCompletePercentage: boolean): boolean {
        return (
            progress.data != null ||
            progress.finish ||
            progress.uuid === null ||
            (!ignoreCompletePercentage && progress.percentage >= 100) ||
            progress.status === ProgressStatusType.Completed ||
            progress.status === ProgressStatusType.Failed
        );
    }
}
